/* eslint-disable no-underscore-dangle */
/* eslint-disable no-console */
/** @jsx h */

import { Component } from 'preact';
import { PropTypes } from 'preact-compat';
import { connect } from 'preact-redux';
import { EventEmitter } from 'eventemitter3';
import { LogLevel } from '@bugfender/sdk';

import { selectSignInError, selectSignInProcessed } from '../../../reducers/sign-in';
import {
  selectApplicationIsInitialised,
  selectIsUnsupportedMobileOs,
  selectApplicationIsWebview,
  selectApplicationDetected,
} from '../../../reducers/application';
import { selectLoggedInUser, selectLoggedInUserAuth, selectLoggedInUsersDevice } from '../../../reducers/user';
import {
  appVersion, LOCAL_STORAGE, EVENT_BUS_EVENTS,
} from '../../../utils/consts';
import { updateDeviceForCurrentUser } from '../../../api/users';
// eslint-disable-next-line import/no-cycle
import {
  CALL_FAILED_EVENT,
  INCOMING_CALL_STARTED_EVENT,
  OUTGOING_CALL_STARTED_EVENT,
  CALL_FINISHED_EVENT,
} from '../../../utils/connection';
import * as Logger from '../../../utils/logger';

const IdleJS = require('idle-js');

const __FILE__ = 'components\\global\\device-activity\\index.js';

const ATTEMPTS = 3;
const RETRY_TIMEOUT_MS = 5000;
const IDLE_TIMEOUT_JS = 3 * 60 * 1000;
const MIN_DEVICE_ACTIVITY_REPORT_INTERVAL = 15 * 60 * 1000;

class DeviceActivity extends Component {
  constructor(props) {
    super(props);

    this.idleTrackingSuspended = false;
  }

  componentDidMount() {
    this.idle = new IdleJS({
      idle: IDLE_TIMEOUT_JS,
      events: ['click', 'mousemove', 'mouseenter', 'keydown', 'scroll', 'mousewheel', 'touchmove', 'touchstart', 'mousedown'],
      onActive: () => {
        try {
          this.updateDeviceActivity();
        } finally {
          this.lastWentIdle = null;
        }
      },
      onShow: () => {
        try {
          this.updateDeviceActivity();
        } finally {
          this.idle.reset({ idle: false, visible: true });
          this.lastWentIdle = false;
        }
      },
      onIdle: () => {
        this.lastWentIdle = Date.now();
        this.reportIdlePresence();
      },
    });

    this.lastWentIdle = null;

    const { callEvents } = this.props;

    callEvents.on(CALL_FAILED_EVENT, this.onCallFinished, this);
    callEvents.on(INCOMING_CALL_STARTED_EVENT, this.onCallStarted, this);
    callEvents.on(OUTGOING_CALL_STARTED_EVENT, this.onCallStarted, this);
    callEvents.on(CALL_FINISHED_EVENT, this.onCallFinished, this);

    this.idle.start();
  }

  shouldComponentUpdate() {
    return false;
  }

  componentWillUnmount() {
    this.idle && this.idle.stop();

    const { callEvents } = this.props;

    callEvents.removeListener(CALL_FAILED_EVENT, this.onCallFinished, this);
    callEvents.removeListener(INCOMING_CALL_STARTED_EVENT, this.onCallStarted, this);
    callEvents.removeListener(OUTGOING_CALL_STARTED_EVENT, this.onCallStarted, this);
    callEvents.removeListener(CALL_FINISHED_EVENT, this.onCallFinished, this);
  }

  onCallStarted = () => {
    const __METHOD__ = 'DeviceActivity::onCallStarted';
    Logger.logWithTag(
      __FILE__,
      __METHOD__,
      Logger.TAG_PRESENCE,
      'A call has started - stopping idle tracking',
      LogLevel.Info,
      undefined,
      true,
    );

    const { idle } = this;
    idle && idle.stop();

    this.idleTrackingSuspended = true;

    // As we will not get any onActive / onShow notifications till we resume idle tracking,
    // we have to explicitly report the "available" presence.
    // And we can add the 'On call' additional info, while at that :)
    this.props.sendPresence('On call');
  }

  onCallFinished = () => {
    const __METHOD__ = 'DeviceActivity::onCallFinished';

    if (this.idleTrackingSuspended) {
      Logger.logWithTag(
        __FILE__,
        __METHOD__,
        Logger.TAG_PRESENCE,
        'A call has finished - resuming idle tracking',
        LogLevel.Info,
        undefined,
        true,
      );

      // Withdraw the 'On call' status
      this.props.sendPresence();

      const { idle } = this;
      idle && idle.reset({
        idle: false,
        visible: !document.hidden,
      }).start();

      this.idleTrackingSuspended = false;
    }
  }

  reportIdlePresence() {
    const __METHOD__ = 'DeviceActivity::reportIdlePresence';
    const { sendPresence, isConnected, eventBus } = this.props;

    if (this.isAppAnActiveDeviceNow() && isConnected) {
      Logger.logWithTag(
        __FILE__,
        __METHOD__,
        Logger.TAG_PRESENCE,
        'Idle state detected - setting XMPP presence as away',
        LogLevel.Info,
        undefined,
        true,
      );

      sendPresence('Away', 'away');
      eventBus.emit(EVENT_BUS_EVENTS.PRESENCE_CHANGED_TO_AWAY);
    }
  }

  wasIdleLongEnoughToSendDeviceActivityUpdate() {
    return this.lastWentIdle && ((Date.now() - this.lastWentIdle) >= MIN_DEVICE_ACTIVITY_REPORT_INTERVAL);
  }

  async updateDeviceActivity(attempts = ATTEMPTS) {
    const __METHOD__ = 'DeviceActivity::updateDeviceActivity';

    if (this.isAppAnActiveDeviceNow()) {
      const deviceId = localStorage.getItem(LOCAL_STORAGE.UUID);
      const { numericDeviceId, eventBus } = this.props;

      if (deviceId && numericDeviceId) {
        const { detected, userAuth, isConnected, sendPresence } = this.props;
        const { name, version } = detected;
        const currentMoment = Date.now();
        const currentMomentAsISOString = new Date(currentMoment).toISOString();

        try {
          if (!isConnected) {
            return;
          }

          Logger.logWithTag(
            __FILE__,
            __METHOD__,
            Logger.TAG_PRESENCE,
            'User activity detected - setting XMPP presence as available',
            LogLevel.Info,
            undefined,
            true,
          );

          sendPresence();

          // Publish an event so that other components could update themselves (e.g. refresh the chat history
          // or re-read the list of recent activities).
          eventBus.emit(EVENT_BUS_EVENTS.PRESENCE_CHANGED_TO_ONLINE);

          if (this.lastWentIdle === null || this.wasIdleLongEnoughToSendDeviceActivityUpdate()) {
            Logger.logWithTag(
              __FILE__,
              __METHOD__,
              Logger.TAG_PRESENCE,
              'User activity detected - sending a device activity update',
              LogLevel.Info,
              undefined,
              true,
            );

            await updateDeviceForCurrentUser(
              userAuth,
              numericDeviceId,
              {
                device: deviceId,
                agent: name,
                agentVersion: version,
                type: 'WEB',
                locale: navigator.languages[0],
                appVersion,
                msDate: currentMoment,
                date: currentMomentAsISOString,
              },
            );
          }
        } catch (err) {
          Logger.logWithTag(
            __FILE__,
            __METHOD__,
            Logger.TAG_PRESENCE,
            `Failed to send the device activity update. Remaining attempts: ${attempts}`,
            LogLevel.Warning,
            err,
            true,
          );

          if (attempts > 0) {
            setTimeout(() => this.updateDeviceActivity(attempts - 1), RETRY_TIMEOUT_MS);
          }
        }
      } else {
        Logger.logWithTag(
          __FILE__,
          __METHOD__,
          Logger.TAG_PRESENCE,
          `No device ID, cannot send a device activity update. Device UUID: ${deviceId}, back-end device ID: ${numericDeviceId}`,
          LogLevel.Error,
          undefined,
          false,
        );
      }
    }
  }

  isAppAnActiveDeviceNow() {
    const __METHOD__ = 'DeviceActivity::isAppAnActiveDeviceNow';
    const { isWebview, isUnsupportedMobileOs, applicationInitialised, signInProcessed, signInError, userData } = this.props;

    try {
      return (!isUnsupportedMobileOs || isWebview) && applicationInitialised && (signInProcessed && !signInError) && userData && userData.userAuth;
    } catch (e) {
      Logger.logWithTag(
        __FILE__,
        __METHOD__,
        Logger.TAG_PRESENCE,
        'Unexpected error occurred while checking if the app is an active device',
        LogLevel.Error,
        e,
        false,
      );

      return false;
    }
  }

  render() {
    return null; // This is intentionally a renderless component
  }
}

DeviceActivity.propTypes = {
  isConnected: PropTypes.bool.isRequired,
  applicationInitialised: PropTypes.bool.isRequired,
  isUnsupportedMobileOs: PropTypes.bool.isRequired,
  isWebview: PropTypes.bool.isRequired,
  signInProcessed: PropTypes.bool.isRequired,
  signInError: PropTypes.string.isRequired,
  userData: PropTypes.shape({
    id: PropTypes.number,
    userAuth: PropTypes.string,
    pro: PropTypes.shape({}),
  }).isRequired,
  userAuth: PropTypes.string, // eslint-disable-line react/require-default-props
  numericDeviceId: PropTypes.number, // eslint-disable-line react/require-default-props
  detected: PropTypes.shape({
    name: PropTypes.string,
    version: PropTypes.string,
  }).isRequired,
  sendPresence: PropTypes.func.isRequired,
  eventBus: PropTypes.instanceOf(EventEmitter).isRequired,
  callEvents: PropTypes.instanceOf(EventEmitter).isRequired,
};

const mapStateToProps = (state) => ({
  applicationInitialised: selectApplicationIsInitialised(state),
  isUnsupportedMobileOs: selectIsUnsupportedMobileOs(state),
  isWebview: selectApplicationIsWebview(state),
  signInProcessed: selectSignInProcessed(state),
  signInError: selectSignInError(state),
  userData: selectLoggedInUser(state),
  userAuth: selectLoggedInUserAuth(state),
  numericDeviceId: selectLoggedInUsersDevice(state),
  detected: selectApplicationDetected(state),
  logInFailure: state.logInFailure,
  currentUrl: state.url.url,
});

export default connect(
  mapStateToProps,
  null,
)(DeviceActivity);



// WEBPACK FOOTER //
// ./components/global/device-activity/index.js